import { useSession } from '../session/FournisseurSession';
import { nomCompte } from '../session/permissions';
import { EnTetePage } from '../commun/EnTetePage';
import { INSTITUTIONS_SUPERVISION } from '@eec/contrats';
import { PagePilotageReseaux } from './incidents/PagePilotageReseaux';
import { CarteIncidents, type IncidentLocalise } from './incidents/CarteIncidents';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../api/client';
import { messageErreur } from '../api/erreurs';
import { peut } from '../session/permissions';
import {
  INCIDENT_STATUS_LABELS,
  PRIORITY_LABELS,
  type IncidentStatus,
  type Priority,
} from '@eec/contrats';

export function PageAccueil() {
  const { contexte } = useSession();
  const espace = contexte?.appartenances.find((a) => a.id === contexte.appartenance_active?.id);
  const incidents = useQuery({
    queryKey: ['incidents-veille', contexte?.appartenance_active?.id],
    queryFn: () =>
      requete<{ elements: (IncidentLocalise & { priorite: Priority })[] }>('/incidents'),
    enabled: espace?.organisation?.type === 'exploitant' && peut(contexte, 'incident', 'voir'),
    refetchInterval: 60_000,
  });
  const synthese = useQuery({
    queryKey: ['synthese-exploitation', contexte?.appartenance_active?.id],
    staleTime: 0,
    queryFn: () =>
      requete<{
        incidents: { statut: IncidentStatus; priorite: Priority; nombre: number }[];
        signalements: { a_qualifier: number; total: number };
        interventions: { statut: string; nombre: number }[];
        calcule_le: string;
      }>('/incidents/synthese'),
    enabled: peut(contexte, 'indicateurs', 'voir') && espace?.organisation?.type === 'exploitant',
  });
  return (
    <section>
      <EnTetePage titre="EauÉnergie Connect" />
      <p>Bienvenue {nomCompte(contexte)}.</p>
      <p>
        Espace actif :{' '}
        <strong>{espace?.organisation?.nom ?? espace?.institution ?? 'Non sélectionné'}</strong>.
      </p>
      {contexte?.appartenance_active?.institution &&
      (INSTITUTIONS_SUPERVISION as readonly string[]).includes(
        contexte.appartenance_active.institution,
      ) ? (
        <PagePilotageReseaux accueil />
      ) : (
        <p>Retrouvez vos incidents, interventions et ressources dans la navigation.</p>
      )}
      {synthese.error && <p role="alert">{messageErreur(synthese.error)}</p>}
      {synthese.data && (
        <div className="eec-veille">
          <section>
            <h2>Situation de l’exploitation</h2>
            <div className="eec-statistiques">
              <article>
                <h3>Signalements à qualifier</h3>
                <p className="eec-statistique-valeur">{synthese.data.signalements.a_qualifier}</p>
                <p>Sur {synthese.data.signalements.total} signalement(s) reçu(s).</p>
              </article>
              <article>
                <h3>Interventions enregistrées</h3>
                <p className="eec-statistique-valeur">
                  {synthese.data.interventions.reduce((n, i) => n + i.nombre, 0)}
                </p>
                <p>Tous états confondus dans cet espace.</p>
              </article>
              <article>
                <h3>Incidents enregistrés</h3>
                <p className="eec-statistique-valeur">
                  {synthese.data.incidents.reduce((n, i) => n + i.nombre, 0)}
                </p>
                <p>Répartition par état et priorité ci-dessous.</p>
              </article>
            </div>
            {incidents.data && (
              <>
                <h3>File d’incidents</h3>
                <ul className="eec-incidents-liste">
                  {incidents.data.elements
                    .filter((i) => !['CLOSED', 'RESTORED_VERIFIED'].includes(i.statut))
                    .slice(0, 5)
                    .map((i) => (
                      <li key={i.id} data-priorite={i.priorite}>
                        <Link to={`/incidents/${i.id}`}>{i.titre}</Link>
                        <br />
                        <small>
                          {INCIDENT_STATUS_LABELS[i.statut as IncidentStatus]} ·{' '}
                          {PRIORITY_LABELS[i.priorite]}
                        </small>
                      </li>
                    ))}
                </ul>
                <Link to="/incidents">Voir tous les incidents →</Link>
              </>
            )}
            <details>
              <summary>Répartition par état et priorité</summary>
              <div
                className="eec-tableau-defilant"
                role="region"
                aria-label="Répartition des incidents"
                tabIndex={0}
              >
                <table>
                  <caption>Incidents par état et priorité</caption>
                  <thead>
                    <tr>
                      <th scope="col">État</th>
                      <th scope="col">Priorité</th>
                      <th scope="col">Nombre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {synthese.data.incidents.map((i) => (
                      <tr key={`${i.statut}-${i.priorite}`}>
                        <td>{INCIDENT_STATUS_LABELS[i.statut]}</td>
                        <td>{PRIORITY_LABELS[i.priorite]}</td>
                        <td>{i.nombre}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
            {synthese.data.incidents.length === 0 && <p>Aucun incident enregistré.</p>}
            <p>Calculé le {new Date(synthese.data.calcule_le).toLocaleString('fr-FR')}.</p>
          </section>
          <div className="eec-veille-carte">
            {incidents.data ? (
              <CarteIncidents
                incidents={incidents.data.elements.filter(
                  (i) => !['CLOSED', 'RESTORED_VERIFIED'].includes(i.statut),
                )}
              />
            ) : (
              <p>Chargement de la carte des incidents…</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
